import { CloseOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { twMerge } from 'tailwind-merge'
import { useToast } from '../contexts/ToastContext'
import type { Toast as ToastType } from '../contexts/ToastContext'

const icons = {
  success: CheckCircleOutlined,
  error: CloseCircleOutlined,
  warning: WarningOutlined,
  info: ExclamationCircleOutlined,
}

const styles: Record<ToastType['variant'], string> = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-brand-50 border-brand-200 text-brand-800',
}

const iconStyles: Record<ToastType['variant'], string> = {
  success: 'text-emerald-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-brand-600',
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastType
  onDismiss: () => void
}) {
  const Icon = icons[toast.variant]
  return (
    <div
      role="alert"
      className={twMerge(
        'flex items-start gap-3 rounded-xl border px-4 py-3 shadow-card min-w-[320px] max-w-[420px]',
        styles[toast.variant]
      )}
    >
      <Icon className={twMerge('w-5 h-5 shrink-0 mt-0.5', iconStyles[toast.variant])} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{toast.message}</p>
        {toast.action ? (
          <button
            type="button"
            onClick={toast.action.onClick}
            className="mt-2 text-sm font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded-lg hover:bg-black/10 transition-colors"
        aria-label="Dismiss"
      >
        <CloseOutlined className="w-4 h-4" />
      </button>
    </div>
  )
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToast()
  return (
    <div className="fixed bottom-4 right-4 z-[1400] flex flex-col gap-2 pointer-events-none">
      <div className="flex flex-col gap-2 pointer-events-auto">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
        ))}
      </div>
    </div>
  )
}
